import { cancelOffer, retryOffer } from "./api";
import type { SubscriptionCharge, SubscriptionOffer } from "./types";
import { ICONS, dangerButton, errorBox, escapeHtml, hint, loadingRow, primaryButton, summaryList } from "./ui";

function intervalLabel(seconds: number): string {
  if (seconds >= 2592000 && seconds % 2592000 === 0) {
    const n = seconds / 2592000;
    return n === 1 ? "every month" : `every ${n} months`;
  }
  if (seconds >= 86400 && seconds % 86400 === 0) {
    const n = seconds / 86400;
    return n === 1 ? "every day" : `every ${n} days`;
  }
  if (seconds >= 3600 && seconds % 3600 === 0) {
    const n = seconds / 3600;
    return n === 1 ? "every hour" : `every ${n} hours`;
  }
  return `every ${seconds}s`;
}

export function when(ts?: number | null): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function explorerTx(chainId: number, hash?: string | null): string | null {
  if (!hash) return null;
  const bases: Record<number, string> = {
    1: "https://etherscan.io/tx/",
    137: "https://polygonscan.com/tx/",
    56: "https://bscscan.com/tx/",
    42161: "https://arbiscan.io/tx/",
    8453: "https://basescan.org/tx/",
    11155111: "https://sepolia.etherscan.io/tx/",
    80002: "https://amoy.polygonscan.com/tx/",
  };
  const base = bases[chainId];
  return base ? `${base}${hash}` : null;
}

function chargeMeta(status: string): { label: string; tone: string; icon: string } {
  const value = (status || "").toLowerCase();
  if (value === "paid" || value === "success") {
    return { label: "Paid", tone: "paid", icon: ICONS.check };
  }
  if (value === "pending" || value === "processing" || value === "confirming") {
    return { label: "Pending", tone: "pending", icon: ICONS.clock };
  }
  if (value === "failed" || value === "error") {
    return { label: "Failed", tone: "failed", icon: ICONS.x };
  }
  if (value === "expired") {
    return { label: "Expired", tone: "muted", icon: ICONS.clock };
  }
  const label = value ? value.replace(/_/g, " ") : "Charge";
  return { label: label[0].toUpperCase() + label.slice(1), tone: "muted", icon: ICONS.clock };
}

function formatAmount(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

function chargeRow(offer: SubscriptionOffer, charge: SubscriptionCharge): string {
  const meta = chargeMeta(charge.status);
  const link = explorerTx(offer.chain_id, charge.tx_hash);
  const amount = formatAmount(charge.invoice_amount || offer.approve_amount);
  const tx = link
    ? `<a class="charge-tx" href="${escapeHtml(link)}" target="_blank" rel="noopener">${ICONS.external}<span>View tx</span></a>`
    : "";
  return `<article class="charge-item is-${meta.tone}">
    <div class="charge-ico" aria-hidden="true">${meta.icon}</div>
    <div class="charge-main">
      <strong>${escapeHtml(meta.label)}</strong>
      <small>${escapeHtml(when(charge.created_at))}</small>
    </div>
    <div class="charge-side">
      <span class="charge-amount">${escapeHtml(amount)} ${escapeHtml(offer.token_symbol)}</span>
      ${tx}
    </div>
  </article>`;
}

function visibleCharges(offer: SubscriptionOffer): SubscriptionCharge[] {
  return (offer.charges || []).filter((charge) => {
    const status = (charge.status || "").toLowerCase();
    return status !== "expired" && status !== "failed" && status !== "error";
  });
}

export function retryMessage(offer: SubscriptionOffer): { title: string; body: string } {
  const symbol = offer.token_symbol || "token";
  const amount = `${offer.amount_fiat} ${offer.currency_fiat}`;
  switch (offer.last_error_code) {
    case "insufficient_balance":
      return {
        title: "Not enough balance",
        body:
          offer.last_error ||
          `The wallet does not have enough ${symbol} to pay ${amount}. Top up and retry the payment.`,
      };
    case "insufficient_allowance":
      return {
        title: "Allowance is too low",
        body:
          offer.last_error ||
          `The wallet allowance is too low to pull ${symbol}. Approve again, then retry.`,
      };
    default:
      return {
        title: "Payment did not go through",
        body: offer.last_error || "The last charge could not be completed. You can try again.",
      };
  }
}

export function isRetryable(offer: SubscriptionOffer): boolean {
  return offer.status === "past_due";
}

export function isCancelScheduled(offer: SubscriptionOffer): boolean {
  return Boolean(offer.cancel_at) && offer.status !== "cancelled" && offer.status !== "exhausted";
}

export function manageBody(offer: SubscriptionOffer, extra = ""): string {
  const retryable = isRetryable(offer);
  const ending = isCancelScheduled(offer);
  const canCancel =
    !ending && ["active", "past_due", "pending_approve", "confirming"].includes(offer.status);
  const charges = retryable ? visibleCharges(offer) : offer.charges || [];
  const retry = retryable ? retryMessage(offer) : null;
  const list = charges.length
    ? `<section class="charge-list">
        <div class="charge-head">
          <h3>Charges</h3>
          <span class="charge-count">${charges.length}</span>
        </div>
        ${charges.map((c) => chargeRow(offer, c)).join("")}
      </section>`
    : retryable
    ? ""
    : `<p class="hint">No charges yet. The first pull runs after approve, then on the timer.</p>`;
  const details = retryable
    ? ""
    : summaryList([
        { label: "Status", value: ending ? "active · cancels at period end" : offer.status },
        ...(offer.customer_name ? [{ label: "Name", value: offer.customer_name }] : []),
        ...(offer.customer_email ? [{ label: "Email", value: offer.customer_email }] : []),
        ...(offer.reference ? [{ label: "Reference", value: offer.reference }] : []),
        { label: "Amount", value: `${offer.amount_fiat} ${offer.currency_fiat}` },
        { label: "Frequency", value: intervalLabel(offer.interval_seconds) },
        {
          label: "Charged",
          value:
            offer.cycles_authorized > 0
              ? `${offer.cycles_charged} / ${offer.cycles_authorized}`
              : `${offer.cycles_charged} / until cancelled`,
        },
        ending
          ? { label: "Access until", value: when(offer.cancel_at) }
          : { label: "Next charge", value: when(offer.next_charge_at) },
      ]);
  const retryBlock = retry
    ? `<div class="retry-panel">
        <h2 class="retry-title">${escapeHtml(retry.title)}</h2>
        <p class="retry-sub">${escapeHtml(retry.body)}</p>
        ${primaryButton("retry-pay", "Retry payment")}
      </div>`
    : "";
  const cancelHint = ending
    ? hint("Cancellation is scheduled. Access stays until the paid period ends. No further charges will be pulled.")
    : hint("Cancel keeps access until the paid period ends, then stops future Flowgate pulls. Also revoke the token allowance in your wallet if you want to block the spender completely.");
  return `
    ${details}
    ${retryBlock}
    ${list}
    ${canCancel ? dangerButton("cancel-sub", "Cancel subscription") : ""}
    ${cancelHint}
    ${extra}
  `;
}

export function bindManage(root: HTMLElement, offer: SubscriptionOffer, onUpdate: (next: SubscriptionOffer) => void): void {
  root.querySelector("#retry-pay")?.addEventListener("click", () => {
    const btn = root.querySelector<HTMLButtonElement>("#retry-pay");
    if (btn) btn.disabled = true;
    void (async () => {
      try {
        onUpdate(await retryOffer(offer.subscription_id));
      } catch (error) {
        if (btn) btn.disabled = false;
        const msg = error instanceof Error ? error.message : "Retry failed";
        root.insertAdjacentHTML("beforeend", errorBox(msg));
      }
    })();
  });
  root.querySelector("#cancel-sub")?.addEventListener("click", () => {
    if (!window.confirm("Cancel this subscription? You'll keep access until the paid period ends. No further charges.")) return;
    const btn = root.querySelector<HTMLButtonElement>("#cancel-sub");
    if (btn) btn.disabled = true;
    void (async () => {
      try {
        onUpdate(await cancelOffer(offer.subscription_id));
      } catch (error) {
        if (btn) btn.disabled = false;
        const msg = error instanceof Error ? error.message : "Cancel failed";
        root.insertAdjacentHTML("beforeend", errorBox(msg));
      }
    })();
  });
}

export function manageLoading(label: string): string {
  return loadingRow(label);
}
